import PageHeader from "../../components/Header/PageHeader";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {fetchWrapper} from "../../helpers";
import {
    Layout, Row, Col, Card, Space, Typography, Button, Badge, Divider,
    Select, Input, Segmented, Spin, Pagination
} from "antd";

import {PlanCard} from "../ContentPlan/PlanCard";
import {mergeStateObject} from "../../utils/merger";
import {useOrganization} from "../../context/organizationContext";
import {useSelector} from "react-redux";
import AppNavigation from "../../components/AppNavigation";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

const ModerationPage = () => {
    const user = useSelector((obj) => obj.auth.user);
    const org = useOrganization();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [isPostsLoading, setIsPostsLoading] = useState(true);
    const [filter, setFilter] = useState("user_approved");
    const [pagination, setPagination] = useState({
        page: 1,
        size: +localStorage.getItem("paginationSize") || 10,
        count: 0,
    });
    const [pagesByFilter, setPagesByFilter] = useState({
        user_approved: 1,
        moderator_approved: 1,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [orgUsers, setOrgUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const buildQueryString = React.useCallback(() => {
        const params = new URLSearchParams();
        params.set('page', pagination.page);
        params.set('limit', pagination.size);
        params.set('approvedExtended', filter);
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);

        if (selectedUserId) params.set('userId', selectedUserId);

        if (searchQuery && searchQuery.trim() !== '') {
            params.set('search', searchQuery.trim());
        }

        return params.toString();
    }, [pagination.page, pagination.size, filter, sortBy, sortOrder, selectedUserId, searchQuery]);

    useEffect(() => {
        let cancelled = false;
        setIsPostsLoading(true);

        fetchWrapper
            .get(`${baseUrl}/post/moderation?${buildQueryString()}`)
            .then((res) => {
                if (cancelled) return;
                setPosts(res?.posts || []);
                mergeStateObject(setPagination, {count: res?.count || 0});
            })
            .catch(() => {
                if (cancelled) return;
                setPosts([]);
            })
            .finally(() => {
                if (cancelled) return;
                setIsPostsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        buildQueryString,
    ]);

    useEffect(() => {
        const id = setTimeout(() => {
            mergeStateObject(setPagination, { page: 1 });
        }, 500);
        return () => clearTimeout(id);
    }, [searchQuery]);

    useEffect(() => {
        mergeStateObject(setPagination, {page: 1});
    }, [sortBy, sortOrder]);

    useEffect(() => {
        mergeStateObject(setPagination, {page: 1});
    }, [selectedUserId, filter]);

    useEffect(() => {
        if (org?.organization?.employees) {
            setOrgUsers(org.organization.employees);
        }
    }, [org]);

    // 1) Пока org/user не загружены — ничего не редиректим
    const isOrgLoaded = !!org?.organization;
    const isUserLoaded = user !== undefined && user !== null

    useEffect(() => {
        if (!isOrgLoaded || !isUserLoaded) return;
        if (!(org.organization?.moderatorExist && user?.moderatorOrg)) {
            navigate("/", {replace: true});
        }
    }, [isOrgLoaded, isUserLoaded, org?.organization, user, navigate])

    if (!isOrgLoaded || !isUserLoaded) {
        return (
            <Layout style={{minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center"}}>
                <Spin size="large" tip={t("functions.loading")}/>
            </Layout>
        );
    }

    return (
        <Layout style={{minHeight: "100vh", background: "linear-gradient(180deg, #ffffff, #faf5ff)"}}>
            {/* TOP BAR */}
            <ImeanHeader initialDark={true}></ImeanHeader>
            <StickyNavigationHeader headerTitle={t("moderation.header")} onBackClick={() => navigate("/home")} actions={<Button type="primary" onClick={() => navigate("/post-distribution")}>{t("postDistribution.postDistributionTagName")}</Button>}></StickyNavigationHeader>

            {/* CONTENT */}
            <Layout.Content style={{maxWidth: 1200, margin: "24px auto", width: "100%", padding: "0 16px"}}>
                {/* Toolbar */}
                <Card className="shadow-sm rounded-xl" bodyStyle={{ padding: 12 }}>
                    {/* MOBILE */}
                    <div className="flex flex-col gap-2 md:hidden">
                        {/* Filters */}
                        <div className="flex flex-col gap-2">
                            <Select
                                placeholder={t("functions.selectUser")}
                                value={selectedUserId}
                                onChange={(v) => setSelectedUserId(v)}
                                className="w-full"
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                                options={[
                                    { value: "", label: t("functions.allUsers") },
                                    ...orgUsers.map((u) => ({ value: u.id, label: u.fullName || u.email })),
                                ]}
                            />
                            <Input.Search
                                allowClear
                                placeholder={t("functions.searchPosts")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onSearch={() => mergeStateObject(setPagination, { page: 1 })}
                                className="w-full"
                            />
                        </div>

                        {/* Sort row — горизонтальный скролл */}
                        <div className="flex flex-col gap-2 py-1">
                            <label className="text-[12px] text-gray-500">{t("functions.sortBy")}:</label>
                            <Select
                                size="small"
                                value={sortBy}
                                onChange={(v) => setSortBy(v)}
                                className="w-full"
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: "date", label: t("functions.date") },
                                    { value: "user", label: t("functions.user") },
                                ]}
                            />

                            <label className="text-[12px] text-gray-500">{t("functions.order")}:</label>
                            <Select
                                size="small"
                                value={sortOrder}
                                onChange={(v) => setSortOrder(v)}
                                className="w-full"
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: "asc", label: t("functions.ascending") },
                                    { value: "desc", label: t("functions.descending") },
                                ]}
                            />
                        </div>

                        {/* Segmented — full width on mobile */}
                        <Segmented
                            value={filter}
                            onChange={(val) => {
                                if (val !== filter) {
                                    setIsPostsLoading(true);
                                    setFilter(val);
                                    mergeStateObject(setPagination, { page: pagesByFilter[val] ?? 1 });
                                }
                            }}
                            options={[
                                { label: t("moderation.onModeration"), value: "user_approved" },
                                { label: t("moderation.archive"), value: "moderator_approved" },
                            ]}
                            block
                        />
                    </div>

                    {/* DESKTOP/TABLET - Inline Layout */}
                    <div className="hidden md:flex md:items-center md:gap-3 md:flex-wrap">
                        {/* User Select */}
                        <Select
                            placeholder={t("functions.selectUser")}
                            value={selectedUserId}
                            onChange={(v) => setSelectedUserId(v)}
                            style={{ minWidth: 200 }}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={[
                                { value: "", label: t("functions.allUsers") },
                                ...orgUsers.map((u) => ({ value: u.id, label: u.fullName || u.email })),
                            ]}
                        />

                        {/* Search Input */}
                        <Input.Search
                            allowClear
                            placeholder={t("functions.searchPosts")}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onSearch={() => mergeStateObject(setPagination, { page: 1 })}
                            style={{ minWidth: 240 }}
                        />

                        {/* Divider */}
                        <Divider type="vertical" className="!h-6 !m-0" />

                        {/* Sort By */}
                        <div className="flex items-center gap-2">
                            <Typography.Text type="secondary" className="!text-sm whitespace-nowrap">
                                {t("functions.sortBy")}:
                            </Typography.Text>
                            <Select
                                value={sortBy}
                                onChange={(v) => setSortBy(v)}
                                style={{ width: 120 }}
                                size="small"
                                options={[
                                    { value: "date", label: t("functions.date") },
                                    { value: "user", label: t("functions.user") },
                                ]}
                            />
                        </div>

                        {/* Order */}
                        <div className="flex items-center gap-2">
                            <Typography.Text type="secondary" className="!text-sm whitespace-nowrap">
                                {t("functions.order")}:
                            </Typography.Text>
                            <Select
                                value={sortOrder}
                                onChange={(v) => setSortOrder(v)}
                                style={{ width: 110 }}
                                size="small"
                                options={[
                                    { value: "asc", label: t("functions.ascending") },
                                    { value: "desc", label: t("functions.descending") },
                                ]}
                            />
                        </div>

                        {/* Divider */}
                        <Divider type="vertical" className="!h-6 !m-0" />

                        {/* Segmented Filter */}
                        <Segmented
                            size="small"
                            value={filter}
                            onChange={(val) => {
                                if (val !== filter) {
                                    setIsPostsLoading(true);
                                    setFilter(val);
                                    mergeStateObject(setPagination, { page: pagesByFilter[val] ?? 1 });
                                }
                            }}
                            options={[
                                { label: t("moderation.onModeration"), value: "user_approved" },
                                { label: t("moderation.archive"), value: "moderator_approved" },
                            ]}
                        />
                    </div>
                </Card>

                {/* List */}
                <div style={{marginTop: 16}}>
                    {isPostsLoading ? (
                        <Card className="shadow-sm" bodyStyle={{padding: 32, textAlign: "center"}}>
                            <Space direction="vertical" align="center">
                                <Spin size="large"/>
                                <Typography.Text type="secondary">{t("functions.loading")}</Typography.Text>
                            </Space>
                        </Card>
                    ) : posts?.length ? (
                        <Row gutter={[16, 16]}>
                            {posts.map((p, i) => (
                                <Col xs={24} key={p?.id || i}>
                                    {/* Render as PlanCard in moderation mode; keep your existing PlanCard logic */}
                                    <PlanCard
                                        item={{
                                            ...(p?.ContentPlan || p),
                                            id: (p?.ContentPlan?.id || p?.id),
                                            uniqueIndexId: p?.id
                                        }}
                                        index={i}
                                        isModeration={true}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <Card className="shadow-sm" bodyStyle={{padding: 32, textAlign: "center"}}>
                            <Space direction="vertical">
                                <Typography.Title level={5} style={{margin: 0}}>
                                    {filter === "user_approved"
                                        ? t("moderation.emptyModerationListTitle") || t("functions.nothingFound")
                                        : t("moderation.emptyArchiveTitle") || t("functions.nothingFound")}
                                </Typography.Title>
                                <Typography.Text type="secondary">
                                    {filter === "user_approved"
                                        ? t("moderation.emptyModerationListHint") || ""
                                        : t("moderation.emptyArchiveHint") || ""}
                                </Typography.Text>
                            </Space>
                        </Card>
                    )}
                </div>

                {/* Pagination */}
                {(posts?.length > 0 || pagination.page > 1) && (
                    <Card className="shadow-sm" style={{marginTop: 16}} bodyStyle={{padding: 16}}>
                        <Row justify="center">
                            <Pagination
                                size={window.innerWidth < 520 ? "small" : "default"}
                                hideOnSinglePage
                                disabled={isPostsLoading}
                                onChange={(page, pageSize) => {
                                    localStorage.setItem("paginationSize", pageSize);
                                    mergeStateObject(setPagination, {page});
                                    setPagesByFilter((prev) => ({...prev, [filter]: page}));
                                    setIsPostsLoading(true);
                                    window.scrollTo({top: 0, behavior: "smooth"});
                                }}
                                current={pagination.page}
                                pageSize={pagination.size}
                                total={pagination.count}
                                showSizeChanger={false}
                            />
                        </Row>
                    </Card>
                )}
            </Layout.Content>

            {/* Mobile app nav spacer + component */}
            <div style={{height: 70}}/>
            <AppNavigation/>
        </Layout>
    );

};

export default ModerationPage;
